import { useEffect, useState } from 'react';
import { KeyRound, ExternalLink } from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 136 — Que el negocio compre su propio crédito.
 *
 * Petición del dueño: *"agregar en configuración una sección para poder actualizar el valor del
 * API-KEY de Claude, para cuando el cliente compre su propio crédito"*.
 *
 * Es lo que contesta la pregunta de negocio que quedó abierta al construir el ayudante: **¿quién
 * paga los tokens cuando haya veinte clientes?** Con esto, cada quien el suyo — y el ayudante deja
 * de ser un costo que crece con la cartera para volverse una función del producto.
 *
 * ## Tres decisiones que se ven en pantalla
 *
 * - **La llave nunca se muestra.** Se ve `sk-ant-…4f2a` y si está puesta o no. Se puede escribir y
 *   borrar; leer, no. Ni siquiera al dueño: una vez guardada, sale de circulación.
 * - **Se prueba antes de guardar.** Una llave con la forma correcta pero sin crédito falla igual, y
 *   descubrirlo en hora pico cuando alguien pregunta es justo lo que hay que evitar.
 * - **Se dice qué pasa si no la pone**, porque es una pregunta legítima y la respuesta es buena:
 *   sigue funcionando con la del sistema.
 */

interface Estado {
  configurada: boolean;
  pista: string | null;
  sePuedeGuardar: boolean;
}

export function LlaveDelAyudante() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ texto: string; error: boolean } | null>(null);

  const cargar = async () => {
    const r = await window.api.config.estadoApiKey();
    if (r.ok) setEstado(r.data);
  };

  useEffect(() => {
    void cargar();
  }, []);

  const guardar = async (nueva: string | null) => {
    setMensaje(null);
    setGuardando(true);
    const r = await window.api.config.guardarApiKey(nueva);
    setGuardando(false);
    if (!r.ok) {
      setMensaje({ texto: traducirErrorBackend(r.error, r.codigo), error: true });
      return;
    }
    setValor('');
    await cargar();
    setMensaje({
      texto: nueva
        ? 'Llave guardada y probada. El ayudante ya usa tu crédito.'
        : 'Llave borrada. El ayudante vuelve a usar el crédito del sistema.',
      error: false,
    });
  };

  if (!estado) return null;

  return (
    <div className="card space-y-3">
      <div className="flex items-start gap-2">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
        <div>
          <h3 className="font-bold">Tu llave del ayudante</h3>
          <p className="text-sm text-slate-600">
            Si compras tu propio crédito, el ayudante lo usa y pagas sólo lo tuyo.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
        {estado.configurada ? (
          <span className="text-slate-700">
            Ahora usa tu llave: <span className="font-mono font-semibold">{estado.pista}</span>
          </span>
        ) : (
          <span className="text-slate-700">
            Todavía no pones la tuya. El ayudante funciona con el crédito del sistema.
          </span>
        )}
      </div>

      {!estado.sePuedeGuardar ? (
        /*
          Sin clave maestra en el servidor no hay dónde cifrarla, y ofrecer el campo sería
          prometer algo que va a fallar al guardar. Se dice, y se dice a quién avisar.
        */
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Este servidor todavía no puede guardar llaves de forma segura. Avísale a soporte antes de
          comprar crédito.
        </p>
      ) : (
        <>
          <div>
            <label className="label" htmlFor="apiKeyAyudante">
              Pega aquí tu llave
            </label>
            <input
              id="apiKeyAyudante"
              className="input font-mono"
              type="password"
              autoComplete="off"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="sk-ant-..."
              maxLength={200}
            />
            <p className="help-text">
              Se guarda cifrada y no se vuelve a mostrar. La comprobamos antes de guardarla, así
              sabes de inmediato si sirve.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void guardar(valor.trim())}
              className="btn-primary min-h-[44px]"
              disabled={guardando || valor.trim().length < 20}
            >
              {guardando ? 'Comprobando…' : 'Guardar llave'}
            </button>
            {estado.configurada && (
              <button
                onClick={() => void guardar(null)}
                className="btn-ghost min-h-[44px] border border-slate-300"
                disabled={guardando}
              >
                Quitar mi llave
              </button>
            )}
            <a
              href="https://console.anthropic.com/settings/keys"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost min-h-[44px] border border-slate-300"
            >
              <ExternalLink className="h-4 w-4" />
              Comprar crédito
            </a>
          </div>
        </>
      )}

      {mensaje && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            mensaje.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
          role={mensaje.error ? 'alert' : undefined}
        >
          {mensaje.texto}
        </div>
      )}
    </div>
  );
}
